import { BarChart2, FileText, Home, Settings, Users, Sparkles, Book, ChevronLeft, ChevronRight, FolderKanban, BookOpen, Database } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Projects', href: '/projects', icon: FolderKanban },
  { name: 'Wiki', href: '/wiki', icon: BookOpen },
  { name: 'Prompts', href: '/prompts', icon: Sparkles },
  { name: 'Batch Functions', href: '/batch-functions', icon: Database },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Stop Words', href: '/stop-words', icon: Book },
  { name: 'API Services', href: '/api-services', icon: Settings },
];

interface SidebarProps {
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

export function Sidebar({ isCollapsed, onCollapse }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();

  // Get user role
  const userRole = 
    user?.user_metadata?.role || 
    user?.role ||
    (user?.email === 'andmaillev@gmail.com' ? 'admin' : 'client');

  // Filter navigation items based on user role
  const filteredNavigation = navigation.filter(item => {
    // Admin sees everything
    if (userRole === 'admin' || user?.email === 'andmaillev@gmail.com') {
      return true;
    }

    // Hide API Services from non-admins
    if (item.name === 'API Services') {
      return false;
    }

    // Show Users to admins and optimizers
    if (item.name === 'Users') {
      return userRole === 'optimizer';
    }

    // Show Prompts, Stop Words, and Batch Functions to optimizers and admins
    if (item.name === 'Prompts' || item.name === 'Stop Words' || item.name === 'Batch Functions') {
      return userRole === 'optimizer' || userRole === 'admin';
    }

    // Show other items to everyone
    return true;
  });

  return (
    <div 
      className={cn(
        "fixed inset-y-0 left-0 flex flex-col bg-white border-r border-gray-200 transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex-1 flex flex-col pt-20 pb-4 overflow-y-auto">
        <nav className="flex-1 px-2 space-y-1">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  isActive
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                )}
                title={isCollapsed ? item.name : undefined}
              >
                <item.icon
                  className={cn(
                    isActive
                      ? "text-gray-500"
                      : "text-gray-400 group-hover:text-gray-500",
                    "flex-shrink-0 h-5 w-5",
                    isCollapsed ? "mx-auto" : "mr-3"
                  )}
                />
                {!isCollapsed && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>
      </div>
      <button
        onClick={() => onCollapse(!isCollapsed)}
        className="absolute top-20 -right-3 bg-white border border-gray-200 rounded-full p-1 shadow-sm hover:bg-gray-50"
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronLeft className="h-4 w-4 text-gray-400" />
        )}
      </button>
    </div>
  );
}